import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Eye, Globe, Link2, ChevronDown, Maximize2, Minimize2, ArrowLeft } from 'lucide-react';
import { useT } from '../../i18n';
import './Chatbot.css';

const Chatbot = ({ accountId }) => {
  const { t } = useT();
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: t('chatbot.greeting')
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  
  // PIX Flow State
  const [showPixForm, setShowPixForm] = useState(false);
  const [pixData, setPixData] = useState({ pixKey: '', amount: '', description: '' });
  const [pixStep, setPixStep] = useState(1); // 1: key, 2: amount, 3: confirm
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: textToSend }]);
    setIsLoading(true);

    try {
      // Get accountId from props or localStorage
      const currentAccountId = accountId || parseInt(localStorage.getItem('accountId')) || 1;

      const response = await fetch('/api/chatbot/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: textToSend,
          accountId: currentAccountId,
          sessionId: `session-${Date.now()}`,
          history: messages.slice(-10).map(m => ({ role: m.role, content: m.content }))
        })
      });

      const data = await response.json();
      
      // Check if response suggests PIX action
      const shouldShowPixForm = textToSend.toLowerCase().includes('pix') || 
                                textToSend.toLowerCase().includes('transferir') ||
                                textToSend.toLowerCase().includes('enviar dinheiro');
      
      // Add assistant response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.message || t('chatbot.fallback_message'),
        action: data.action,
        actionData: data.actionData,
        showPixButton: shouldShowPixForm
      }]);

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('chatbot.error_message')
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startPixFlow = () => {
    setShowPixForm(true);
    setPixStep(1);
    setPixData({ pixKey: '', amount: '', description: '' });
  };

  const handlePixSubmit = () => {
    if (pixStep === 1 && pixData.pixKey) {
      setPixStep(2);
    } else if (pixStep === 2 && pixData.amount) {
      setPixStep(3);
    } else if (pixStep === 3) {
      // Confirm PIX
      const descLine = pixData.description ? `📝 ${pixData.description}` : '';
      setMessages(prev => [...prev,
        { role: 'user', content: t('chatbot.pix_user_msg', { amount: pixData.amount, key: pixData.pixKey }) },
        { role: 'assistant', content: t('chatbot.pix_started', { amount: pixData.amount, key: pixData.pixKey, desc: descLine }) }
      ]);
      setShowPixForm(false);
      setPixData({ pixKey: '', amount: '', description: '' });
      setPixStep(1);
    }
  };

  const cancelPixFlow = () => {
    setShowPixForm(false);
    setPixData({ pixKey: '', amount: '', description: '' });
    setPixStep(1);
  };

  // Quick action buttons
  const quickActions = [
    { label: t('chatbot.qa_balance'), message: t('chatbot.qa_balance_msg') },
    { label: t('chatbot.qa_extract'), message: t('chatbot.qa_extract_msg') },
    { label: t('chatbot.qa_pix'), action: startPixFlow },
    { label: t('chatbot.qa_help'), message: t('chatbot.qa_help_msg') },
  ];

  // Vulnerability examples - Real LLM security attacks (OWASP LLM Top 10)
  const vulnerabilityExamples = [
    {
      icon: Eye,
      title: 'Jailbreak (DAN)',
      message: t('chatbot.vuln_jailbreak_msg'),
    },
    {
      icon: Globe,
      title: 'System Prompt Leak',
      message: t('chatbot.vuln_leak_msg'),
    },
    {
      icon: Link2,
      title: 'Instruction Override',
      message: t('chatbot.vuln_override_msg'),
    },
  ];

  // PIX Form Component
  const PixForm = () => (
    <div className="pix-form-overlay">
      <div className="pix-form">
        <div className="pix-form-header">
          <button className="pix-back" onClick={cancelPixFlow}>
            <ArrowLeft size={20} />
          </button>
          <h3>{t('chatbot.pix_title')}</h3>
          <div className="pix-steps">
            <span className={`step ${pixStep >= 1 ? 'active' : ''}`}>1</span>
            <span className={`step ${pixStep >= 2 ? 'active' : ''}`}>2</span>
            <span className={`step ${pixStep >= 3 ? 'active' : ''}`}>3</span>
          </div>
        </div>
        
        <div className="pix-form-content">
          {pixStep === 1 && (
            <div className="pix-step">
              <label>{t('chatbot.pix_key_label')}</label>
              <input
                type="text"
                placeholder={t('chatbot.pix_key_placeholder')}
                value={pixData.pixKey}
                onChange={(e) => setPixData({...pixData, pixKey: e.target.value})}
                autoFocus
              />
              <div className="pix-suggestions">
                <span onClick={() => setPixData({...pixData, pixKey: 'pedro.silva@dogbank.com'})}>
                  pedro.silva@dogbank.com
                </span>
                <span onClick={() => setPixData({...pixData, pixKey: 'vitoria.itadori@dogbank.com'})}>
                  vitoria.itadori@dogbank.com
                </span>
              </div>
            </div>
          )}
          
          {pixStep === 2 && (
            <div className="pix-step">
              <label>{t('chatbot.pix_amount_label')}</label>
              <div className="pix-amount-input">
                <span className="currency">R$</span>
                <input
                  type="number"
                  placeholder="0,00"
                  value={pixData.amount}
                  onChange={(e) => setPixData({...pixData, amount: e.target.value})}
                  autoFocus
                />
              </div>
              <div className="pix-quick-amounts">
                {[10, 20, 50, 100, 200].map(val => (
                  <button key={val} onClick={() => setPixData({...pixData, amount: val.toString()})}>
                    R$ {val}
                  </button>
                ))}
              </div>
              <label style={{marginTop: '16px'}}>{t('chatbot.pix_desc_label')}</label>
              <input
                type="text"
                placeholder={t('chatbot.pix_desc_placeholder')}
                value={pixData.description}
                onChange={(e) => setPixData({...pixData, description: e.target.value})}
              />
            </div>
          )}
          
          {pixStep === 3 && (
            <div className="pix-step pix-confirm">
              <h4>{t('chatbot.pix_confirm_title')}</h4>
              <div className="pix-summary">
                <div className="summary-row">
                  <span>{t('chatbot.pix_recipient')}</span>
                  <strong>{pixData.pixKey}</strong>
                </div>
                <div className="summary-row">
                  <span>{t('chatbot.pix_amount')}</span>
                  <strong className="amount">R$ {pixData.amount}</strong>
                </div>
                {pixData.description && (
                  <div className="summary-row">
                    <span>{t('chatbot.pix_description')}</span>
                    <strong>{pixData.description}</strong>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="pix-form-footer">
          {pixStep > 1 && (
            <button className="pix-btn secondary" onClick={() => setPixStep(pixStep - 1)}>
              {t('chatbot.back')}
            </button>
          )}
          <button 
            className="pix-btn primary" 
            onClick={handlePixSubmit}
            disabled={
              (pixStep === 1 && !pixData.pixKey) ||
              (pixStep === 2 && !pixData.amount)
            }
          >
            {pixStep === 3 ? t('chatbot.confirm_pix') : t('chatbot.continue')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button 
          className="chatbot-fab"
          onClick={() => setIsOpen(true)}
          title={t('chatbot.open_dogbot')}
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className={`chatbot-container ${isMaximized ? 'maximized' : ''}`}>
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <Bot size={20} />
              </div>
              <div>
                <h3>DogBot</h3>
                <span className="chatbot-status">
                  <span className="status-dot"></span>
                  {t('chatbot.online')}
                </span>
              </div>
            </div>
            <div className="header-actions">
              <button 
                className="header-btn"
                onClick={() => setIsMaximized(!isMaximized)}
                title={isMaximized ? t('chatbot.minimize') : t('chatbot.maximize')}
              >
                {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button 
                className="header-btn close"
                onClick={() => setIsOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* PIX Form Overlay */}
          {showPixForm && <PixForm />}

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`chatbot-message ${msg.role}`}
              >
                {msg.role === 'assistant' && (
                  <div className="message-avatar">
                    <Bot size={16} />
                  </div>
                )}
                <div className="message-bubble">
                  <div className="message-content">{msg.content}</div>
                  {msg.showPixButton && (
                    <button className="inline-pix-btn" onClick={startPixFlow}>
                      {t('chatbot.start_pix')}
                    </button>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="message-avatar user">
                    <User size={16} />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-message assistant">
                <div className="message-avatar">
                  <Bot size={16} />
                </div>
                <div className="message-bubble">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="chatbot-actions">
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  className="quick-btn"
                  onClick={() => action.action ? action.action() : sendMessage(action.message)}
                  disabled={isLoading}
                >
                  {action.label}
                </button>
              ))}
            </div>

            {/* Vulnerability Examples Toggle */}
            <button 
              className="examples-toggle"
              onClick={() => setShowExamples(!showExamples)}
            >
              <span>{t('chatbot.vuln_examples')}</span>
              <ChevronDown 
                size={16} 
                className={`toggle-icon ${showExamples ? 'open' : ''}`}
              />
            </button>

            {/* Vulnerability Examples Cards */}
            {showExamples && (
              <div className="vulnerability-examples">
                {vulnerabilityExamples.map((example, index) => (
                  <button
                    key={index}
                    className="vulnerability-card"
                    onClick={() => sendMessage(example.message)}
                    disabled={isLoading}
                  >
                    <div className="card-icon">
                      <example.icon size={20} />
                    </div>
                    <div className="card-content">
                      <span className="card-title">{example.title}</span>
                      <span className="card-message">"{example.message}"</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <input
              type="text"
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chatbot.input_placeholder')}
              disabled={isLoading}
            />
            <button 
              className="send-btn"
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
